import type { SVGProps } from "react";

/**
 * Navigation glyphs.
 *
 * Hand-drawn 20×20 strokes rather than an icon package: a handful of icons do not
 * justify a dependency, and inline SVG inherits `currentColor` so each one
 * picks up the active/inactive colour without extra classes.
 */

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="size-[18px] shrink-0"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Dashboard — the day at a glance, drawn as four panes. */
export function DashboardIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="2.5" width="6.5" height="6.5" rx="1.4" />
      <rect x="11" y="2.5" width="6.5" height="4" rx="1.4" />
      <rect x="2.5" y="11" width="6.5" height="6.5" rx="1.4" />
      <rect x="11" y="8.5" width="6.5" height="9" rx="1.4" />
    </Icon>
  );
}

/** Daily tasks — a checked list. */
export function TasksIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 5.5 4.4 7 7 4" />
      <path d="M3 13.5 4.4 15 7 12" />
      <path d="M9.5 5.5H17" />
      <path d="M9.5 13.5H17" />
    </Icon>
  );
}

/**
 * Done — a ticked circle with a trail of earlier ticks behind it.
 *
 * Not a second checked list: that is Tasks. The circle is the tick a row turns
 * into, and the arc behind it is the history. Now only the Done view's empty
 * state uses it — Done is reached from the Tasks header, not the rail.
 */
export function DoneIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="10" r="6.5" />
      <path d="m8.25 10 2 2 3.5-4" />
      <path d="M3.25 6.5a8.5 8.5 0 0 0 0 7" />
    </Icon>
  );
}

/**
 * Inbox — a tray with something dropping into it.
 *
 * Deliberately not an envelope: the tray reads as "work waiting to be sorted",
 * which is what this surface is, while an envelope reads as mail and would
 * promise a reply box that does not exist.
 */
export function InboxIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.75 11.5 5 4.5h10l2.25 7" />
      <path d="M2.75 11.5h3.5l1 2h5.5l1-2h3.5v3.5a1.5 1.5 0 0 1-1.5 1.5H4.25a1.5 1.5 0 0 1-1.5-1.5z" />
    </Icon>
  );
}

/** AgentDesk — an agent terminal handing a checked result to its owner. */
export function AgentDeskIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="4.5" width="15" height="12.5" rx="2" />
      <path d="M10 2.5v2M6.25 9h.01M13.75 9h.01M6.75 13h6.5" />
    </Icon>
  );
}

/** More — the compact tab bar's doorway to secondary sections. */
export function MoreIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="4.5" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="10" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="10" r="1" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Project kanban — lanes of cards. */
/** Ads Queue — a play button in a slot, the ads waiting their turn. */
export function AdsQueueIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="3.5" width="15" height="9.5" rx="1.6" />
      <path d="m8.5 6.3 3.6 2-3.6 2z" />
      <path d="M5 16.5h10" />
    </Icon>
  );
}

/** Organic Queue — a post card with a sprout: content that grows without paid reach. */
export function OrganicQueueIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="3.5" width="15" height="13" rx="1.6" />
      <path d="M10 13.5v-4" />
      <path d="M10 10.2c0-1.9 1.4-3.2 3.3-3.2 0 1.9-1.4 3.2-3.3 3.2z" />
      <path d="M10 11.4c0-1.5-1.1-2.6-2.6-2.6 0 1.5 1.1 2.6 2.6 2.6z" />
    </Icon>
  );
}

/** Office — a desk with a monitor, seen from the front. */
export function OfficeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="6" y="3" width="8" height="6" rx="1" />
      <path d="M10 9v2.5" />
      <path d="M2.5 12h15" />
      <path d="M4.5 12v5M15.5 12v5" />
    </Icon>
  );
}

export function KanbanIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="3" width="5" height="9" rx="1.4" />
      <rect x="12.5" y="3" width="5" height="14" rx="1.4" />
    </Icon>
  );
}

/** Kanban City - four isometric blocks rising from a shared map. */
export function KanbanCityIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m3 7 4-2.25L11 7 7 9.25zM9 10.5l4-2.25 4 2.25-4 2.25z" />
      <path d="M3 7v3l4 2.25 4-2.25V7M9 10.5v3l4 2.25 4-2.25v-3" />
      <path d="M7 9.25v3M13 12.75v3" />
    </Icon>
  );
}

/** Workout — a dumbbell. */
export function WorkoutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7.5v5" />
      <path d="M16 7.5v5" />
      <rect x="5.75" y="5.5" width="2.5" height="9" rx="1.1" />
      <rect x="11.75" y="5.5" width="2.5" height="9" rx="1.1" />
      <path d="M8.25 10h3.5" />
    </Icon>
  );
}

/** Stats — a small bar chart. */
export function StatsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 16.5h14" />
      <path d="M5.75 16.5V11" />
      <path d="M10 16.5V4.5" />
      <path d="M14.25 16.5V8" />
    </Icon>
  );
}

/** Tama-sama — the raised paw and bell of a maneki-neko. */
export function TamaIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.25 8.25V4.5a2 2 0 0 1 4 0V8" />
      <path d="M4 8.5 5.25 6l2 1.5h5.5l2-1.5L16 8.5v4.25a5.25 5.25 0 0 1-10.5 0z" />
      <path d="M8 11.5h.01M12 11.5h.01M8.25 14.25c1.1.75 2.4.75 3.5 0" />
      <circle cx="10" cy="16.5" r="1" />
    </Icon>
  );
}

/**
 * The sidebar-collapse control.
 *
 * One glyph for both directions — a panel with a rail, and an arrow pointing
 * the way the panel is about to move. Drawing "collapse" and "expand" as two
 * different pictures would make the button look like two different buttons.
 */
export function SidebarToggleIcon({ collapsed = false, ...props }: IconProps & { collapsed?: boolean }) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="3.5" width="15" height="13" rx="2" />
      <path d="M7.75 3.5v13" />
      {collapsed ? <path d="M11 8.25 12.75 10 11 11.75" /> : <path d="M14.25 8.25 12.5 10l1.75 1.75" />}
    </Icon>
  );
}

/** The account menu — a person, deliberately generic. */
export function AccountIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="7" r="3.1" />
      <path d="M4 16.5a6 6 0 0 1 12 0" />
    </Icon>
  );
}
